import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Shield, ShieldAlert, Plus, Trash2, Sparkles, Loader2, FileLock2, Skull, Network, Wifi, UserX, Bug, Pencil, Save } from "lucide-react";

const SCENARIO_KINDS = [
  { value: "ransomware", label: "Ransomware", icon: FileLock2, tone: "bg-red-100 text-red-700 border-red-200" },
  { value: "data_breach", label: "Data Breach", icon: ShieldAlert, tone: "bg-rose-100 text-rose-700 border-rose-200" },
  { value: "ddos", label: "DDoS", icon: Network, tone: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "supply_chain", label: "Supply Chain", icon: Wifi, tone: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  { value: "insider_threat", label: "Insider Threat", icon: UserX, tone: "bg-purple-100 text-purple-700 border-purple-200" },
  { value: "zero_day", label: "Zero-Day", icon: Bug, tone: "bg-orange-100 text-orange-700 border-orange-200" },
  { value: "generic_cyber", label: "Generic Cyber", icon: Shield, tone: "bg-slate-100 text-slate-700 border-slate-200" },
];

const severityTone = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700",
};

export default function CyberRecoveryPlansPage() {
  const { api, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [aiDetecting, setAiDetecting] = useState(false);
  // V14 — Edit state
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [form, setForm] = useState({
    title: "",
    scenario_kind: "ransomware",
    description: "",
    severity: "high",
    rto_hours: 8,
    rpo_hours: 1,
    playbook_steps: [""],
  });

  const canAuthor = ["bcm_manager", "it_drp_manager", "it_function_manager", "admin"].includes(user?.role);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cyber-recovery/plans");
      setPlans(r.data?.plans || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not load cyber plans"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of plans) {
      g[p.scenario_kind] = g[p.scenario_kind] || [];
      g[p.scenario_kind].push(p);
    }
    return g;
  }, [plans]);

  const resetForm = () =>
    setForm({
      title: "",
      scenario_kind: "ransomware",
      description: "",
      severity: "high",
      rto_hours: 8,
      rpo_hours: 1,
      playbook_steps: [""],
    });

  const handleAiDetect = async () => {
    if (!form.description.trim()) {
      toast.info("Type a short incident description, then let AI classify it.");
      return;
    }
    setAiDetecting(true);
    try {
      const r = await api.post("/ai-v14/cyber/detect-scenario", {
        incident_description: form.description,
      });
      const d = r.data || {};
      const topTag = (d.scenario_tags || [])[0];
      setForm((f) => ({
        ...f,
        scenario_kind: topTag?.kind || f.scenario_kind,
        severity: d.severity || f.severity,
        playbook_steps: (d.suggested_playbook_steps && d.suggested_playbook_steps.length) ? d.suggested_playbook_steps : f.playbook_steps,
      }));
      toast.success(`AI classified as "${topTag?.kind || "unknown"}" (${Math.round((topTag?.confidence || 0) * 100)}% confidence)`);
    } catch (e) {
      toast.error(extractErrorMessage(e, "AI detection failed"));
    } finally {
      setAiDetecting(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.info("Title is required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/cyber-recovery/plans", {
        ...form,
        playbook_steps: form.playbook_steps.filter((s) => s.trim()),
      });
      toast.success("Cyber recovery plan created");
      setShowCreate(false);
      resetForm();
      loadPlans();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to create plan"));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Delete this cyber recovery plan? This cannot be undone.")) return;
    try {
      await api.delete(`/cyber-recovery/plans/${planId}`);
      toast.success("Plan deleted");
      loadPlans();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to delete plan"));
    }
  };

  // V14 — Open edit dialog for an existing plan
  const openEdit = (plan) => {
    setEditingPlan(plan);
    setEditForm({
      title: plan.title || "",
      description: plan.description || "",
      severity: plan.severity || "high",
      status: plan.status || "draft",
      rto_hours: plan.rto_hours ?? 0,
      rpo_hours: plan.rpo_hours ?? 0,
      playbook_steps: plan.playbook_steps?.length ? [...plan.playbook_steps] : [""],
    });
  };

  const handleSaveEdit = async () => {
    if (!editingPlan || !editForm) return;
    setSavingEdit(true);
    try {
      await api.patch(`/cyber-recovery/plans/${editingPlan.id}`, {
        ...editForm,
        playbook_steps: (editForm.playbook_steps || []).filter((s) => s.trim()),
      });
      toast.success("Plan updated");
      setEditingPlan(null);
      setEditForm(null);
      loadPlans();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to update plan"));
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="cyber-recovery-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-red-600" />
            Cyber Recovery Plans
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Dedicated playbooks for ransomware, data breach, DDoS, and supply-chain events.
            Separate from standard IT-DRP plans per ISO 22301 Annex A.
          </p>
        </div>
        {canAuthor && (
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-red-600 hover:bg-red-700"
            data-testid="new-cyber-plan-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Cyber Plan
          </Button>
        )}
      </div>

      {/* Scenario palette */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Coverage at a glance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {SCENARIO_KINDS.map((s) => {
              const count = (grouped[s.value] || []).length;
              return (
                <div
                  key={s.value}
                  className={`border rounded-lg p-3 ${count > 0 ? s.tone : "bg-slate-50 text-slate-400 border-slate-200"}`}
                  data-testid={`scenario-tile-${s.value}`}
                >
                  <s.icon className="w-5 h-5 mb-1.5" />
                  <div className="text-xs font-semibold">{s.label}</div>
                  <div className="text-lg font-extrabold mt-0.5">{count}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Plans list */}
      {loading ? (
        <div className="animate-pulse h-32 bg-slate-100 rounded-xl" />
      ) : plans.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Skull className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No cyber recovery plans yet.</p>
            {canAuthor && (
              <p className="text-slate-400 text-xs mt-1">
                Click <span className="font-semibold">New Cyber Plan</span> above to create one.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {plans.map((p) => {
            const s = SCENARIO_KINDS.find((x) => x.value === p.scenario_kind);
            const Icon = s?.icon || Shield;
            return (
              <Card key={p.id} data-testid={`cyber-plan-card-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s?.tone || "bg-slate-100 text-slate-600"}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-900 truncate">{p.title}</h3>
                          <Badge className={severityTone[p.severity] || "bg-slate-100 text-slate-700"}>
                            {p.severity}
                          </Badge>
                          <Badge variant="outline">{p.status}</Badge>
                        </div>
                        {p.description && (
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{p.description}</p>
                        )}
                        <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-2">
                          {p.rto_hours != null && <span>RTO: <strong>{p.rto_hours}h</strong></span>}
                          {p.rpo_hours != null && <span>RPO: <strong>{p.rpo_hours}h</strong></span>}
                          {p.playbook_steps?.length ? <span>{p.playbook_steps.length} playbook step{p.playbook_steps.length === 1 ? "" : "s"}</span> : null}
                        </div>
                      </div>
                    </div>
                    {canAuthor && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(p)}
                          className="text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                          data-testid={`edit-cyber-plan-${p.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(p.id)}
                          className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-cyber-plan-${p.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {p.playbook_steps?.length > 0 && (
                    <ol className="mt-3 pl-5 list-decimal space-y-0.5 text-sm text-slate-600">
                      {p.playbook_steps.slice(0, 5).map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                      {p.playbook_steps.length > 5 && (
                        <li className="italic text-slate-400">…and {p.playbook_steps.length - 5} more</li>
                      )}
                    </ol>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) resetForm(); setShowCreate(o); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-600" />
              New Cyber Recovery Plan
            </DialogTitle>
            <DialogDescription>
              Describe the incident and let AI classify the scenario + suggest playbook steps,
              or fill it in manually.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label>Title <span className="text-red-500">*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="E.g. Finance Ransomware Recovery Playbook"
                data-testid="cyber-form-title"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Incident Description</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAiDetect}
                  disabled={aiDetecting}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="ai-detect-btn"
                >
                  {aiDetecting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                  AI classify &amp; suggest playbook
                </Button>
              </div>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="E.g. Multiple finance workstations encrypted, ransom note on each host, backup server suspected compromised."
                rows={3}
                data-testid="cyber-form-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Scenario Kind</Label>
                <select
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  value={form.scenario_kind}
                  onChange={(e) => setForm({ ...form, scenario_kind: e.target.value })}
                  data-testid="cyber-form-scenario"
                >
                  {SCENARIO_KINDS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Severity</Label>
                <select
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  value={form.severity}
                  onChange={(e) => setForm({ ...form, severity: e.target.value })}
                  data-testid="cyber-form-severity"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>RTO (hours)</Label>
                <Input
                  type="number"
                  value={form.rto_hours}
                  onChange={(e) => setForm({ ...form, rto_hours: Number(e.target.value) })}
                  data-testid="cyber-form-rto"
                />
              </div>
              <div className="space-y-1.5">
                <Label>RPO (hours)</Label>
                <Input
                  type="number"
                  value={form.rpo_hours}
                  onChange={(e) => setForm({ ...form, rpo_hours: Number(e.target.value) })}
                  data-testid="cyber-form-rpo"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Playbook Steps</Label>
              {form.playbook_steps.map((step, i) => (
                <div key={i} className="flex gap-2">
                  <div className="w-7 h-7 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-1">
                    {i + 1}
                  </div>
                  <Input
                    value={step}
                    onChange={(e) => {
                      const steps = [...form.playbook_steps];
                      steps[i] = e.target.value;
                      setForm({ ...form, playbook_steps: steps });
                    }}
                    placeholder="E.g. Isolate affected hosts from the network"
                    data-testid={`cyber-form-step-${i}`}
                  />
                  {form.playbook_steps.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const steps = form.playbook_steps.filter((_, idx) => idx !== i);
                        setForm({ ...form, playbook_steps: steps });
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-slate-400" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setForm({ ...form, playbook_steps: [...form.playbook_steps, ""] })}
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add step
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !form.title}
              className="bg-red-600 hover:bg-red-700"
              data-testid="save-cyber-plan-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
              Create Plan
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* V14 — Edit Cyber Plan dialog */}
      <Dialog open={!!editingPlan} onOpenChange={(o) => { if (!o) { setEditingPlan(null); setEditForm(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="edit-cyber-plan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-indigo-600" />
              Edit Cyber Recovery Plan
            </DialogTitle>
            <DialogDescription>
              Update title, severity, status, RTO/RPO and playbook steps.
              Scenario kind is immutable to preserve audit trail.
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  data-testid="edit-form-title"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  data-testid="edit-form-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Severity</Label>
                  <select
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    value={editForm.severity}
                    onChange={(e) => setEditForm({ ...editForm, severity: e.target.value })}
                    data-testid="edit-form-severity"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <select
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    data-testid="edit-form-status"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>RTO (hours)</Label>
                  <Input
                    type="number"
                    value={editForm.rto_hours}
                    onChange={(e) => setEditForm({ ...editForm, rto_hours: Number(e.target.value) })}
                    data-testid="edit-form-rto"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>RPO (hours)</Label>
                  <Input
                    type="number"
                    value={editForm.rpo_hours}
                    onChange={(e) => setEditForm({ ...editForm, rpo_hours: Number(e.target.value) })}
                    data-testid="edit-form-rpo"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Playbook Steps</Label>
                {(editForm.playbook_steps || []).map((step, i) => (
                  <div key={i} className="flex gap-2">
                    <div className="w-7 h-7 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-1">
                      {i + 1}
                    </div>
                    <Input
                      value={step}
                      onChange={(e) => {
                        const steps = [...editForm.playbook_steps];
                        steps[i] = e.target.value;
                        setEditForm({ ...editForm, playbook_steps: steps });
                      }}
                      data-testid={`edit-form-step-${i}`}
                    />
                    {editForm.playbook_steps.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const steps = editForm.playbook_steps.filter((_, idx) => idx !== i);
                          setEditForm({ ...editForm, playbook_steps: steps });
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-slate-400" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditForm({ ...editForm, playbook_steps: [...(editForm.playbook_steps || []), ""] })}
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add step
                </Button>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setEditingPlan(null); setEditForm(null); }}>Cancel</Button>
            <Button
              onClick={handleSaveEdit}
              disabled={savingEdit || !editForm?.title}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-edit-cyber-plan-btn"
            >
              {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
